import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormHelperText, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import { formatNumber } from "@/features/shared";

export const ProductVariantsSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "goodsReceiptProductVariants",
  });

  const productVariants = useWatch({
    control,
    name: "goodsReceiptProductVariants",
  });

  const handleDeleteClick = (id) => () => {
    // 🔍 Find index of the row with the matching id
    const index = productVariants.findIndex((variant) => variant.id === id);

    if (index !== -1) {
      remove(index);
      showSnackbar("Produk berhasil dihapus", "success");
    }
  };

  const processRowUpdate = async (newRow) => {
    return new Promise((resolve, reject) => {
      const { quantity, maxQuantity, invoicedQuantity, returnedQuantity } =
        newRow;

      // Edit rules:
      // 1. quantity must be >= invoicedQuantity
      // 2. quantity must be >= returnedQuantity
      const minQuantity = Math.max(1, invoicedQuantity, returnedQuantity);
      if (quantity < minQuantity) {
        if (invoicedQuantity > 0 && returnedQuantity > 0) {
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah difaktur: ${invoicedQuantity}, sudah diretur: ${returnedQuantity})`
            )
          );
        } else if (invoicedQuantity > 0) {
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah difaktur)`
            )
          );
        } else if (returnedQuantity > 0) {
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah diretur)`
            )
          );
        } else {
          reject(new Error("Kuantitas tidak boleh kurang dari 1"));
        }
        return;
      }

      // Only check maxQuantity if it exists (not null for manual entries)
      if (maxQuantity !== null && quantity > maxQuantity) {
        reject(
          new Error(
            `Kuantitas tidak boleh lebih besar dari permintaan (max: ${maxQuantity})`
          )
        );
        return;
      }

      // Find the index and update the form state
      const index = productVariants.findIndex(
        (variant) => variant.id === newRow.id
      );

      if (index !== -1) {
        update(index, newRow);
        resolve(newRow);
      } else {
        reject(new Error("Produk tidak ditemukan. Muat ulang halaman lalu coba lagi."));
      }
    });
  };

  const handleProcessRowUpdateError = (error) => {
    showSnackbar(error.message, "error");
  };

  const hasAnyConstraints = productVariants.some(
    (variant) => variant.invoicedQuantity > 0 || variant.returnedQuantity > 0
  );

  return (
    <Stack spacing={1}>
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={[
          {
            field: "name",
            headerName: "Nama",
            flex: 5,
            renderCell: ({ row: { name, sku } }) => (
              <ProductVariantNameCell name={name} sku={sku} />
            ),
          },
          {
            field: "quantity",
            headerName: "Qty",
            type: "number",
            valueFormatter: (value) => formatNumber(value),
            flex: 1,
            editable: true,
          },
          { field: "unitOfMeasurement", headerName: "Unit", width: 50 },
          {
            field: "note",
            headerName: "Catatan",
            flex: 1,
            editable: true,
          },
          {
            field: "actions",
            type: "actions",
            headerName: "Aksi",
            getActions: ({
              id,
              row: { invoicedQuantity, returnedQuantity },
            }) => {
              // Delete rules:
              // 1. invoicedQuantity must be 0
              // 2. returnedQuantity must be 0
              const canDelete =
                invoicedQuantity === 0 && returnedQuantity === 0;

              let disabledReason = "";
              if (invoicedQuantity > 0 && returnedQuantity > 0) {
                disabledReason =
                  "Produk tidak dapat dihapus karena sudah difaktur dan sudah diretur";
              } else if (invoicedQuantity > 0) {
                disabledReason =
                  "Produk tidak dapat dihapus karena sudah difaktur";
              } else if (returnedQuantity > 0) {
                disabledReason =
                  "Produk tidak dapat dihapus karena sudah diretur";
              }

              return [
                <Tooltip key={id} title={disabledReason}>
                  <span>
                    <GridActionsCellItem
                      icon={<DeleteIcon />}
                      label="Hapus"
                      className="textPrimary"
                      onClick={handleDeleteClick(id)}
                      color="error"
                      disabled={!canDelete}
                    />
                  </span>
                </Tooltip>,
              ];
            },
          },
        ]}
        rows={productVariants}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        isCellEditable={() => true}
      />
      {hasAnyConstraints && (
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Produk yang sudah difaktur atau diretur memiliki batasan untuk dihapus
          atau diubah kuantitasnya.
        </Typography>
      )}
      {errors.goodsReceiptProductVariants && (
        <FormHelperText error>
          {errors.goodsReceiptProductVariants.message}
        </FormHelperText>
      )}
    </Stack>
  );
};
